import { Activity, Zap, Dumbbell, Heart, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { publicPeptides } from "@/data/peptide-clinical-data";
import { WeightLossGuideLink } from "@/components/seo/WeightLossGuideLink";

const categories = [
  {
    icon: Activity,
    title: "Metabolic Support",
    description: "Supports weight management and metabolic function.",
    detail: "Treatment options may be evaluated by your provider based on your individual needs.",
  },
  {
    icon: Zap,
    title: "Energy & Cellular Health",
    description: "Supports energy levels and overall vitality.",
    detail: "Options are determined based on your symptoms and health profile.",
  },
  {
    icon: Dumbbell,
    title: "Recovery & Performance",
    description: "Supports physical recovery and resilience.",
    detail: "May be considered based on activity level and recovery needs.",
  },
  {
    icon: Heart,
    title: "Hormonal Optimization",
    description: "Supports endocrine balance and overall wellbeing.",
    detail: "Requires provider evaluation and ongoing monitoring.",
  },
];

const PeptidesSection = () => {
  return (
    <section id="peptides" className="py-24 bg-secondary/50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-champagne mb-3">Explore Therapies</p>
          <h2 className="font-display text-4xl md:text-5xl text-foreground mb-4">
            Personalized Therapy Options
          </h2>
          <p className="text-muted-foreground">
            Clinician-guided treatments tailored to your individual health goals and medical profile.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {categories.map((cat) => (
            <Card key={cat.title} className="bg-card border-border hover:shadow-lg transition-shadow duration-300 group">
              <CardContent className="p-8 space-y-4">
                <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center text-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <cat.icon size={24} />
                </div>
                <h3 className="font-display text-xl text-foreground">{cat.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{cat.description}</p>
                <p className="text-xs text-muted-foreground/80 italic leading-relaxed">{cat.detail}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FDA-approved / publicly available medications */}
        <div className="max-w-5xl mx-auto mt-20">
          <div className="text-center max-w-2xl mx-auto mb-6">
            <div className="inline-flex items-center gap-2 mb-3">
              <ShieldCheck size={16} className="text-primary" />
              <p className="text-sm font-semibold uppercase tracking-widest text-primary">
                FDA-Approved & Publicly Available
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {publicPeptides.map((p) => (
              <Card
                key={p.name}
                className="border-border bg-card hover:shadow-md transition-shadow"
              >
                <CardContent className="p-5 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-display text-lg text-foreground leading-tight">{p.name}</h4>
                    <Badge variant="secondary" className="text-[10px] shrink-0">
                      {p.shortLabel}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{p.simple}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Required compliance disclaimer */}
        <div className="max-w-3xl mx-auto mt-10">
          <p className="text-[11px] text-muted-foreground leading-relaxed text-center">
            Medications listed above are prescribed by licensed healthcare providers and, when
            appropriate, fulfilled by U.S.-licensed compounding pharmacies operating under
            Section 503A of the Federal Food, Drug, and Cosmetic Act. These pharmacies are subject
            to state and applicable federal regulatory oversight, and medications are prepared for
            individual patient needs. Additional therapies not FDA-approved for specific
            indications are discussed only with a licensed clinician inside your patient portal.
            Not all patients will qualify. Individual results may vary.
          </p>
        </div>

        <div className="text-center mt-8 space-y-4">
          <p className="text-sm text-muted-foreground">
            Exploring metabolic support? Read our <WeightLossGuideLink />.
          </p>
          <Link to="/get-started">
            <Button variant="outline" size="lg" className="gap-2">
              Start Your 2-Minute Assessment <ArrowRight size={16} />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PeptidesSection;
